// Berry waste weighed on a dolly: the floor scale reads crates + fruit + the
// empty dolly, so net = gross − crates × crate tare − dolly tare. Pure, so the
// reject form and its tests share one rule.
//
// What it does NOT do: it never reads the setting itself. The caller passes the
// dolly tare read from system_config `berry_dolly_tare_kg` (see parseDollyTareKg).

export const DOLLY_TARE_KEY = 'berry_dolly_tare_kg'

export type DollyWeighResult =
  | { ok: true; netKg: number; tareKg: number }
  | { ok: false; reason: 'no_dolly_tare' | 'no_crates' | 'no_gross' | 'not_positive'; tareKg: number }

/** The setting as an operator typed it. Blank, junk, zero or negative = not set. */
export function parseDollyTareKg(raw: string | null | undefined): number | null {
  const n = parseFloat((raw ?? '').trim().replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? n : null
}

export function dollyNet(input: {
  grossKg: number
  crates: number
  crateTareKg: number
  dollyTareKg: number | null
}): DollyWeighResult {
  const { grossKg, crates, crateTareKg, dollyTareKg } = input
  const tareKg = crates * crateTareKg + (dollyTareKg ?? 0)
  if (dollyTareKg === null) return { ok: false, reason: 'no_dolly_tare', tareKg }
  if (!(crates > 0)) return { ok: false, reason: 'no_crates', tareKg }
  if (!(grossKg > 0)) return { ok: false, reason: 'no_gross', tareKg }
  const netKg = grossKg - tareKg
  if (netKg <= 0) return { ok: false, reason: 'not_positive', tareKg }
  return { ok: true, netKg, tareKg }
}

export const DOLLY_BLOCK_MESSAGE: Record<Exclude<DollyWeighResult, { ok: true }>['reason'], string> = {
  no_dolly_tare: 'The empty dolly weight is not set. Ask an admin to set it under Config → Berry intake → Empty dolly weight (kg).',
  no_crates: 'Enter the number of crates on the dolly.',
  no_gross: 'Enter the scale weight.',
  not_positive: 'The scale weight is not more than the crates and dolly. Check the weight and the crate count.',
}

/** "1 240 kg − 60 crates × 1.2 kg − dolly 85 kg = 1 083 kg" */
export function dollySum(grossKg: number, crates: number, crateTareKg: number, dollyTareKg: number, netKg: number): string {
  // Fixed format (no Intl): thousands space, point decimal, same on every device.
  const f = (n: number) => String(Math.round(n * 10) / 10).replace(/\B(?=(\d{3})+(?!\d))/, ' ')
  // The crate tare gets 2 decimals: a 0.25 kg crate shown as 0.3 made the sum
  // on screen disagree with its own result.
  const f2 = (n: number) => String(Math.round(n * 100) / 100)
  return `${f(grossKg)} kg − ${crates} crates × ${f2(crateTareKg)} kg − dolly ${f(dollyTareKg)} kg = ${f(netKg)} kg`
}

/** A stored tare above crates × crate tare by more than this is a dolly. */
const DOLLY_SPLIT_MIN_KG = 0.5

/**
 * Edit path. The reject row keeps only one tare number, so a dolly entry is
 * recognised by what is left after the crates: stored tare − crates × the
 * CURRENT crate tare. Returns the dolly part in kg, or null when this is not a
 * dolly entry (nothing left over, or the crate tare is unknown, so the caller
 * keeps today's behaviour). A blank stored crate count means one crate.
 */
export function splitStoredTare(input: {
  tareKg: number | null
  crates: number | null
  crateTareKg: number | null
}): number | null {
  const { tareKg, crates, crateTareKg } = input
  if (tareKg === null || crateTareKg === null) return null
  const rest = tareKg - Math.max(1, crates ?? 1) * crateTareKg
  return rest > DOLLY_SPLIT_MIN_KG ? rest : null
}
